import {
  BARCODE_VALIDATION_NONE,
  BARCODE_VALIDATION_SCAN,
} from '../../Constants';
import { globalizeSelectors } from '../Utils';
import { BARCODE_MODE_NORMAL } from 'js/Constants';
import type {
  BarcodeAction,
  BarcodeActionError,
  BarcodeActionResult,
  BarcodeActionState,
  BarcodeMode,
  BarcodeValidationState,
  LastScannedProductIds,
} from '../types/BarcodeAction';
/**
 * Just a simple flag to notify the UI that progress modal needs to be shown!
 */

/*
 * action types
 */

const types = {
  RESOLVE_BARCODE: 'BARCODEACTIONS/RESOLVE_BARCODE',
  RESOLVE_BARCODE_PENDING: 'BARCODEACTIONS/RESOLVE_BARCODE_PENDING',
  RESOLVE_BARCODE_FULFILLED: 'BARCODEACTIONS/RESOLVE_BARCODE_FULFILLED',
  RESOLVE_BARCODE_REJECTED: 'BARCODEACTIONS/RESOLVE_BARCODE_REJECTED',
  CLEAR_BARCODE_STATE: 'BARCODEACTIONS/CLEAR_BARCODE_STATE',
  SETMODE: 'BARCODEACTIONS/SETMODE',
  SET_VALIDATION_STATE: 'BARCODEACTIONS/SET_VALIDATION_STATE',
  RESET_VALIDATION_STATE_BETWEEN_CANS:
    'BARCODEACTIONS/RESET_VALIDATION_STATE_BETWEEN_CANS',
  SET_LAST_SCANNED_PRODUCT_IDS: 'BARCODEACTIONS/SET_LAST_SCANNED_PRODUCT_IDS',
  CLEAR_LAST_SCANNED_PRODUCT_IDS:
    'BARCODEACTIONS/CLEAR_LAST_SCANNED_PRODUCT_IDS',
  SETBARCODE: 'BARCODEACTIONS/SETBARCODE',
} as const;

function payloadAs<Payload>(action: BarcodeAction): Payload {
  return action.payload as Payload;
}

/*
 * action creators
 */

const actions = {
  resolveBarcode: (barcode: string): BarcodeAction<string> => ({
    type: types.RESOLVE_BARCODE,
    payload: barcode,
  }),
  resolveBarcodePending: (pending: boolean): BarcodeAction<boolean> => ({
    type: types.RESOLVE_BARCODE_PENDING,
    payload: pending,
  }),
  resolveBarcodeFulfilled: (
    result: BarcodeActionResult
  ): BarcodeAction<BarcodeActionResult> => ({
    type: types.RESOLVE_BARCODE_FULFILLED,
    payload: result,
  }),
  resolveBarcodeRejected: (
    error: BarcodeActionError
  ): BarcodeAction<BarcodeActionError> => ({
    type: types.RESOLVE_BARCODE_REJECTED,
    payload: error,
  }),
  setMode: (mode: BarcodeMode): BarcodeAction<BarcodeMode> => ({
    type: types.SETMODE,
    payload: mode,
  }),
  clearBarcodeState: (): BarcodeAction => ({ type: types.CLEAR_BARCODE_STATE }),
  setValidationState: (
    state: BarcodeValidationState
  ): BarcodeAction<BarcodeValidationState> => ({
    type: types.SET_VALIDATION_STATE,
    payload: state,
  }),
  resetValidationStateBetweenCans: (): BarcodeAction => ({
    type: types.RESET_VALIDATION_STATE_BETWEEN_CANS,
  }),
  setLastScannedProductIds: (
    baseid: number | null,
    canid: number | null
  ): BarcodeAction<LastScannedProductIds> => ({
    type: types.SET_LAST_SCANNED_PRODUCT_IDS,
    payload: { baseid, canid },
  }),
  clearLastScannedProductIds: (): BarcodeAction => ({
    type: types.CLEAR_LAST_SCANNED_PRODUCT_IDS,
  }),
};

const mountPath = 'barcodeaction'; // mount point in global state, must match root reducer

/**
 * Handling login and log out site actions
 */

const initialState: BarcodeActionState = {
  barcodemode: BARCODE_MODE_NORMAL,
  barcode: null,
  barcodeaction: null,
  validation_state: BARCODE_VALIDATION_NONE, // no validation needed
  barcodeaction_error: null,
  fetch_barcodeaction_start: false,
  last_scanned_baseid: null,
  last_scanned_canid: null,
};

function reducer(
  state: BarcodeActionState = initialState,
  action: BarcodeAction
): BarcodeActionState {
  switch (action.type) {
    case types.SETMODE: {
      return {
        ...state,
        barcodemode: payloadAs<BarcodeMode>(action),
      };
    }
    case types.SET_VALIDATION_STATE: {
      return {
        ...state,
        validation_state: payloadAs<BarcodeValidationState>(action),
      };
    }
    case types.RESET_VALIDATION_STATE_BETWEEN_CANS: {
      return {
        ...state,
        validation_state:
          state.validation_state === BARCODE_VALIDATION_NONE
            ? BARCODE_VALIDATION_NONE
            : BARCODE_VALIDATION_SCAN,
      };
    }
    case types.SET_LAST_SCANNED_PRODUCT_IDS: {
      const { baseid, canid } = payloadAs<LastScannedProductIds>(action);
      return {
        ...state,
        last_scanned_baseid: baseid,
        last_scanned_canid: canid,
      };
    }
    case types.CLEAR_LAST_SCANNED_PRODUCT_IDS: {
      return {
        ...state,
        last_scanned_baseid: null,
        last_scanned_canid: null,
      };
    }
    case types.SETBARCODE: {
      return {
        ...state,
        barcode: payloadAs<string>(action),
      };
    }
    case types.CLEAR_BARCODE_STATE: {
      return {
        ...state,
        validation_state: BARCODE_VALIDATION_NONE,
        barcodeaction: null,
        fetch_barcodeaction_start: false,
        barcodeaction_error: null,
        barcode: null,
        last_scanned_baseid: null,
        last_scanned_canid: null,
      };
    }
    case types.RESOLVE_BARCODE_PENDING: {
      return {
        ...state,
        fetch_barcodeaction_start: payloadAs<boolean>(action),
      };
    }
    case types.RESOLVE_BARCODE_REJECTED: {
      return {
        ...state,
        barcodeaction: null,
        fetch_barcodeaction_start: false,
        barcodeaction_error: payloadAs<BarcodeActionError>(action),
      };
    }
    case types.RESOLVE_BARCODE_FULFILLED: {
      const payload = payloadAs<BarcodeActionResult>(action);
      return {
        ...state,
        barcodeaction: payload,
        fetch_barcodeaction_start: false,
        barcodeaction_error: null,
      };
    }

    default: {
      return state;
    }
  }
}

// selectors

const localSelectors = {
  barcodeaction: (
    state: BarcodeActionState
  ): BarcodeActionState['barcodeaction'] => state.barcodeaction,
  barcodeaction_error: (
    state: BarcodeActionState
  ): BarcodeActionState['barcodeaction_error'] => state.barcodeaction_error,
  barcodemode: (state: BarcodeActionState): BarcodeMode => state.barcodemode,
  last_scanned_baseid: (
    state: BarcodeActionState
  ): BarcodeActionState['last_scanned_baseid'] => state.last_scanned_baseid,
  last_scanned_canid: (
    state: BarcodeActionState
  ): BarcodeActionState['last_scanned_canid'] => state.last_scanned_canid,
  validation_state: (state: BarcodeActionState): BarcodeValidationState =>
    state.validation_state,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export { types as actionTypes, actions as default, selectors, reducer };
